import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Truck, Plus, Search, Filter, Download, Lock, AlertTriangle, Mail, Phone, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { endpoints } from "@/lib/api";
import SupplierModal from "@/components/suppliers/SupplierModal";

const STATUS_TONE = {
  Pending: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:border-slate-500/25",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/25",
  Conditional: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/25",
  Rejected: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/25",
  Suspended: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/25",
};

const GRADE_TONE = {
  A: "bg-gradient-to-br from-emerald-500 to-teal-500 text-white",
  B: "bg-gradient-to-br from-blue-500 to-indigo-500 text-white",
  C: "bg-gradient-to-br from-amber-500 to-orange-500 text-white",
  D: "bg-gradient-to-br from-rose-500 to-red-500 text-white",
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

const isPoorPerformer = (s) =>
  (s.current_rating != null && s.current_rating < 60) ||
  ["Rejected", "Suspended"].includes(s.approval_status);

export default function SuppliersPage() {
  const [params, setParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [authorized, setAuthorized] = useState(null);
  const [q, setQ] = useState("");
  const [clientSlug, setClientSlug] = useState(params.get("client") || "all");
  const [status, setStatus] = useState("all");
  const [modal, setModal] = useState({ open: false, id: null });

  const load = () => {
    const p = {};
    if (clientSlug !== "all") p.client_slug = clientSlug;
    return endpoints.listSuppliers(p).then(setSuppliers).catch(() => {});
  };

  useEffect(() => {
    endpoints.me().then((u) => {
      endpoints.listConsultants().then((cs) => {
        const me = cs.find((c) => c.name === u.name);
        setAuthorized(u.canonical_role === "admin" || u.canonical_role === "staff" || !!me?.is_manager);
      });
    });
    endpoints.listClients().then(setClients).catch(() => {});
  }, []);

  useEffect(() => {
    if (authorized) load();
    const next = new URLSearchParams(params);
    if (clientSlug === "all") next.delete("client"); else next.set("client", clientSlug);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, clientSlug]);

  const filtered = useMemo(() => suppliers.filter((s) => {
    if (status !== "all") {
      if (status === "poor") { if (!isPoorPerformer(s)) return false; }
      else if (s.approval_status !== status) return false;
    }
    if (q && !`${s.name} ${s.products_services} ${s.contact_person} ${s.contact_email} ${s.client_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [suppliers, q, status]);

  const stats = useMemo(() => {
    const s = { total: suppliers.length, approved: 0, conditional: 0, poor: 0, pending: 0 };
    suppliers.forEach((sup) => {
      if (sup.approval_status === "Approved") s.approved++;
      else if (sup.approval_status === "Conditional") s.conditional++;
      else if (sup.approval_status === "Pending") s.pending++;
      if (isPoorPerformer(sup)) s.poor++;
    });
    return s;
  }, [suppliers]);

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  if (!authorized) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <div className="glass-card rounded-3xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4"><Lock className="h-5 w-5" /></div>
          <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
          <p className="text-sm text-slate-500">Supplier management is restricted to ISOQuest staff.</p>
        </div>
      </div>
    );
  }

  const csvHref = endpoints.exportSuppliersCsvUrl(clientSlug !== "all" ? { client_slug: clientSlug } : {});

  const StatCard = ({ label, value, tone, testid, onClick, active }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 bg-white/60 dark:bg-slate-900/40 backdrop-blur-md card-hover transition-all ${active ? "ring-2 ring-indigo-500/50" : ""} border-slate-200/70 dark:border-white/10`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-2xl font-extrabold font-display mt-1 ${tone}`}>{value}</div>
    </button>
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-md shadow-amber-500/25">
            <Truck className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Client Supplier Management</h1>
            <p className="text-xs text-slate-500">{filtered.length} of {suppliers.length} suppliers · separate from ISOQuest's own vendors</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm" data-testid="sup-export-csv-button">
            <a href={csvHref} download><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</a>
          </Button>
          <Button data-testid="sup-new-button" className="brand-gradient text-white" size="sm" onClick={() => setModal({ open: true, id: null })}>
            <Plus className="h-4 w-4 mr-1" /> New supplier
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard label="Total" value={stats.total} tone="text-indigo-600" testid="sup-stat-total" onClick={() => setStatus("all")} active={status === "all"} />
        <StatCard label="Approved" value={stats.approved} tone="text-emerald-600" testid="sup-stat-approved" onClick={() => setStatus("Approved")} active={status === "Approved"} />
        <StatCard label="Conditional" value={stats.conditional} tone="text-amber-600" testid="sup-stat-conditional" onClick={() => setStatus("Conditional")} active={status === "Conditional"} />
        <StatCard label="Underperforming" value={stats.poor} tone="text-rose-600" testid="sup-stat-poor" onClick={() => setStatus("poor")} active={status === "poor"} />
        <StatCard label="Pending" value={stats.pending} tone="text-slate-600" testid="sup-stat-pending" onClick={() => setStatus("Pending")} active={status === "Pending"} />
      </div>

      {/* Filters */}
      <div className="glass-card rounded-3xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="sup-search-input" placeholder="Search supplier, products, contact..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={clientSlug} onValueChange={setClientSlug}>
          <SelectTrigger data-testid="sup-client-filter" className="h-9 w-[220px] text-xs"><Filter className="h-3.5 w-3.5 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="sup-status-filter" className="h-9 w-[180px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Conditional">Conditional</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
            <SelectItem value="Suspended">Suspended</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="poor">Underperforming</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Register */}
      {filtered.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">No suppliers match your filters.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((s) => {
            const poor = isPoorPerformer(s);
            return (
              <button
                key={s.id}
                data-testid={`sup-card-${s.id}`}
                onClick={() => setModal({ open: true, id: s.id })}
                className={`glass-card rounded-3xl p-5 text-left card-hover group ${poor ? "ring-2 ring-rose-400/50 bg-rose-50/40 dark:bg-rose-500/10" : ""}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{s.client_name}</div>
                    <h3 className="font-semibold text-sm mt-0.5 flex items-center gap-1.5">
                      {poor && <AlertTriangle className="h-3.5 w-3.5 text-rose-500 shrink-0" />}
                      <span className="truncate">{s.name}</span>
                    </h3>
                    <div className="text-[11px] text-slate-500 truncate mt-0.5">{s.products_services || "—"}</div>
                  </div>
                  {s.rating_grade ? (
                    <div className={`h-11 w-11 rounded-2xl flex flex-col items-center justify-center shrink-0 ${GRADE_TONE[s.rating_grade]}`}>
                      <span className="font-bold text-lg leading-none">{s.rating_grade}</span>
                      <span className="text-[9px] font-mono opacity-90">{s.current_rating}</span>
                    </div>
                  ) : (
                    <div className="h-11 w-11 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 text-[10px] font-semibold text-slate-500">N/A</div>
                  )}
                </div>

                <div className="space-y-1 text-[11px] text-slate-500 mb-3">
                  {s.contact_person && <div className="flex items-center gap-1.5"><User className="h-3 w-3" /> {s.contact_person}</div>}
                  {s.contact_email && <div className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 shrink-0" /> <span className="truncate">{s.contact_email}</span></div>}
                  {s.contact_phone && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {s.contact_phone}</div>}
                </div>

                {s.current_rating != null && (
                  <div className="mb-3">
                    <Progress value={s.current_rating} className="h-1.5" />
                    <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
                      <span>Rating {s.current_rating}/100</span>
                      <span>Grade {s.rating_grade}</span>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[s.approval_status] || ""}`}>{s.approval_status}</Badge>
                  {s.alternative_supplier && (
                    <span className="text-[10px] text-slate-500 italic truncate max-w-[140px]" title={`Alt: ${s.alternative_supplier}`}>Alt: {s.alternative_supplier}</span>
                  )}
                </div>

                <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100 dark:border-white/5">
                  <span>Last {fmtDate(s.last_evaluation)}</span>
                  <span>Next {fmtDate(s.next_evaluation)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <SupplierModal
        open={modal.open}
        supplierId={modal.id}
        defaultClientSlug={clientSlug !== "all" ? clientSlug : null}
        clients={clients}
        onOpenChange={(o) => { if (!o) setModal({ open: false, id: null }); }}
        onChanged={() => { load(); setModal({ open: false, id: null }); }}
      />
    </div>
  );
}
